import { createGameAssets } from "./game-assets.js";
import {
  Application,
  Container,
  Graphics,
  GraphicsContext,
  Text,
  Texture,
} from "./pixi-runtime.js";
import { CatRig } from "./animation/cat-rig.js";
import { measureBoard } from "./board-geometry.js";
import { brushPolygon, crossStrokeProgress, FAILED_CROSS_COLOR, FAILED_CROSS_WEIGHT } from "./animation/brush-cross.js";
import { boardEntrancePose, tileEntranceDelay, TILE_ENTRANCE_MS } from "./animation/board-entrance.js";
import {
  clamp01,
  easeOutBack,
  easeOutCubic,
  lerp,
} from "./animation/easing.js";

const CROSS_FRAMES = 24;
function createBrushContext(progress, seed, failed = false) {
  const context = new GraphicsContext();
  crossStrokeProgress(progress).forEach((amount, stroke) => {
    const polygon = brushPolygon(stroke, amount, seed, failed ? FAILED_CROSS_WEIGHT : 1);
    if (!polygon.length) return;
    context.poly(polygon).fill(failed ? FAILED_CROSS_COLOR : "#fffaf1");
  });
  return context;
}
function createBrushFrames() {
  return Array.from({ length: 3 }, (_, seed) =>
    Array.from({ length: CROSS_FRAMES + 1 }, (_, frame) =>
      createBrushContext(frame / CROSS_FRAMES, seed),
    ),
  );
}

/** Retained Pixi scene. Only changed tiles are synchronized; frames update transforms. */
export class PixiBoardRenderer {
  constructor({
    clock,
    catAsset = createGameAssets("/").catRig,
    reducedMotion = () =>
      matchMedia("(prefers-reduced-motion: reduce)").matches,
  } = {}) {
    this.clock = clock;
    this.catAsset = catAsset;
    this.reducedMotion = reducedMotion;
    this.cells = [];
    this.ready = false;
    this.lastEffect = -1;
  }
  async mount(host) {
    this.host = host;
    this.app = new Application();
    await this.app.init({
      width: host.clientWidth || 320,
      height: host.clientWidth || 320,
      antialias: true,
      backgroundAlpha: 0,
      autoDensity: true,
      resolution: Math.min(devicePixelRatio || 1, 2),
      preference: "webgl",
      powerPreference: "low-power",
      autoStart: false,
      skipExtensionImports: true,
    });
    this.app.canvas.className = "board-canvas";
    this.app.canvas.setAttribute("aria-hidden", "true");
    host.prepend(this.app.canvas);
    const image = new Image();
    image.src = this.catAsset;
    await image.decode();
    this.texture = Texture.from(image);
    this.brushFrames = createBrushFrames();
    this.failedBrushes = Array.from({ length: 3 }, (_, seed) => createBrushContext(1, seed, true));
    this.ready = true;
    this.unsubscribe = this.clock.subscribe(
      (time) => this.tick(time),
      () => this.active(),
    );
    this.resizeObserver = new ResizeObserver(() => {
      if (this.ready && this.model) this.layout();
    });
    this.resizeObserver.observe(host);
    host.classList.add("pixi-ready");
    if (this.model) this.sync(true);
  }
  render(model) {
    const reset = this.model?.key !== model.key || model.effect.newPuzzle;
    this.model = model;
    if (!this.ready) return;
    this.sync(reset);
  }
  createCell(index) {
    const root = new Container();
    const background = new Graphics();
    const mark = new Graphics(this.brushFrames[index % 3][CROSS_FRAMES]);
    mark.visible = false;
    const highlight = new Graphics()
      .roundRect(-47, -47, 94, 94, 7)
      .stroke({ color: "#fff5b3", width: 3 });
    highlight.visible = false;
    const error = new Graphics()
      .roundRect(-47, -47, 94, 94, 7)
      .stroke({ color: "#cf7565", width: 3 });
    error.visible = false;
    root.addChild(background, mark, highlight, error);
    this.app.stage.addChild(root);
    return {
      root,
      background,
      mark,
      highlight,
      error,
      previous: {},
      index,
      pressScale: 1,
      entranceScale: 1,
      entranceY: 0,
      entranceAlpha: 1,
      entranceAt: null,
      brushFrame: CROSS_FRAMES,
    };
  }
  destroyScene() {
    for (const cell of this.cells) {
      cell.root.removeChild(cell.mark);
      cell.mark.destroy({ context: false });
      cell.root.destroy({ children: true });
    }
    this.cells = [];
  }
  sync(reset = false) {
    const { cells, effect, revision } = this.model;
    if (reset || this.cells.length !== cells.length) {
      this.destroyScene();
      this.cells = cells.map((_, index) => this.createCell(index));
      this.lastEffect = -1;
      this.layout(false);
      if (!this.reducedMotion()) {
        for (const view of this.cells) {
          view.entranceAt = this.clock.time;
          this.sampleEntrance(view, this.clock.time);
        }
        this.clock.burst(TILE_ENTRANCE_MS + (this.model.size - 1) * 68 + 100);
      }
    }
    const fresh = revision !== this.lastEffect;
    cells.forEach((cell, index) => {
      const view = this.cells[index],
        previous = view.previous;
      if (previous.color !== cell.color)
        view.background
          .clear()
          .roundRect(-50, -50, 100, 100, 8)
          .fill(cell.color);
      if (previous.mark !== cell.mark || previous.auto !== cell.auto || previous.failed !== cell.failed) {
        view.mark.visible = cell.mark;
        view.mark.alpha = cell.auto ? 0.45 : 1;
        view.crossAt =
          cell.mark && !cell.auto && !cell.failed && !reset && !this.reducedMotion()
            ? this.clock.time
            : null;
        view.brushFrame = view.crossAt === null ? CROSS_FRAMES : 0;
        view.mark.context = cell.failed
          ? this.failedBrushes[index % 3]
          : this.brushFrames[index % 3][view.brushFrame];
      }
      if (cell.cat && !view.rig) {
        view.rig = new CatRig(this.texture, index + 1);
        view.root.addChild(view.rig.container);
      }
      if (view.rig) {
        view.rig.setVisible(cell.cat);
        if (
          cell.cat &&
          !previous.cat &&
          !reset &&
          effect.placementIndex === index &&
          !this.reducedMotion()
        )
          view.rig.reveal(this.clock.time);
        view.rig.update(this.clock.time, this.reducedMotion());
      }
      if (previous.label !== cell.label) {
        if (cell.label && !view.label) {
          view.label = new Text({
            text: cell.label,
            style: { fontFamily: "Arial", fontSize: 13, fill: "#fffef8" },
          });
          view.label.position.set(-43, -44);
          view.root.addChild(view.label);
        }
        if (view.label) {
          view.label.text = cell.label || "";
          view.label.visible = Boolean(cell.label);
        }
      }
      view.highlight.visible =
        effect.hintIndex === index || Boolean(effect.hintIndices?.includes(index));
      if (fresh && effect.errorIndex === index) {
        view.errorUntil = this.clock.time + 650;
        view.error.visible = true;
      }
      view.previous = cell;
    });
    if (fresh && effect.errorIndex !== undefined) {
      for (const cell of this.cells)
        if (cell.rig?.container.visible) cell.rig.react("sad", this.clock.time);
      this.clock.burst(1450);
    } else if (fresh && effect.placementIndex !== undefined) {
      for (const cell of this.cells)
        if (cell.rig?.container.visible)
          cell.rig.react("happy", this.clock.time);
      this.clock.burst(1150);
    }
    if (
      this.cells.some(
        (cell) => cell.crossAt !== null && cell.crossAt !== undefined,
      )
    )
      this.clock.burst(350);
    this.lastEffect = revision;
    this.app.render();
    this.clock.wake();
  }
  layout(render = true) {
    if (!this.model) return;
    const geometry = measureBoard(this.host);
    if (!geometry.width || !geometry.height) return;
    this.app.renderer.resize(geometry.width, geometry.height);
    this.cells.forEach((view, index) => {
      const tile = geometry.tiles[index];
      const width = tile.width * geometry.scaleX;
      const height = tile.height * geometry.scaleY;
      view.scaleX = width / 100;
      view.scaleY = height / 100;
      view.x = (tile.left - geometry.left) * geometry.scaleX + width / 2;
      view.y = (tile.top - geometry.top) * geometry.scaleY + height / 2;
      this.scaleCell(view);
      if (view.label)
        view.label.style.fontSize = Math.max(8, width * 0.12) / view.scaleX;
    });
    if (render) this.app.render();
  }
  scaleCell(view) {
    view.root.scale.set(
      view.scaleX * view.pressScale * view.entranceScale,
      view.scaleY * view.pressScale * view.entranceScale,
    );
    view.root.position.set(view.x, view.y + view.entranceY);
    view.root.alpha = view.entranceAlpha;
  }
  sampleEntrance(view, time) {
    const pose = boardEntrancePose(time - view.entranceAt, view.index, this.model.size, this.reducedMotion());
    view.entranceScale = pose.scale;
    view.entranceY = pose.y;
    view.entranceAlpha = pose.alpha;
    if (pose.done) view.entranceAt = null;
    this.scaleCell(view);
  }
  setPressed(index, pressed) {
    const view = this.cells[index];
    if (!view || !this.ready) return;
    if (this.reducedMotion()) return;
    const current = this.samplePress(view, this.clock.time);
    view.pressMotion = {
      from: pressed ? current : Math.min(current, 0.94),
      to: pressed ? 0.9 : 1,
      start: this.clock.time,
      duration: pressed ? 85 : 235,
      release: !pressed,
    };
    view.pressScale = view.pressMotion.from;
    this.scaleCell(view);
    this.clock.burst(350);
  }
  samplePress(view, time) {
    if (!view.pressMotion) return view.pressScale;
    const motion = view.pressMotion,
      progress = clamp01((time - motion.start) / motion.duration);
    return lerp(
      motion.from,
      motion.to,
      motion.release ? easeOutBack(progress, 1.1) : easeOutCubic(progress),
    );
  }
  active() {
    if (!this.ready) return false;
    return this.cells.some(
      (view) =>
        view.entranceAt !== null || view.pressMotion ||
        (view.crossAt !== null && view.crossAt !== undefined) ||
        view.errorUntil > this.clock.time ||
        (view.rig?.container.visible &&
          (!this.reducedMotion() || view.rig.motion.reacting(this.clock.time))),
    );
  }
  tick(time) {
    let dirty = false;
    for (const view of this.cells) {
      if (view.entranceAt !== null) {
        this.sampleEntrance(view, time);
        dirty = true;
      }
      if (view.pressMotion) {
        view.pressScale = this.samplePress(view, time);
        this.scaleCell(view);
        if (time - view.pressMotion.start >= view.pressMotion.duration) {
          view.pressScale = view.pressMotion.to;
          this.scaleCell(view);
          view.pressMotion = null;
        }
        dirty = true;
      }
      if (view.crossAt !== null && view.crossAt !== undefined) {
        const progress = clamp01((time - view.crossAt) / 290),
          frame = Math.min(CROSS_FRAMES, Math.ceil(progress * CROSS_FRAMES));
        if (frame !== view.brushFrame) {
          view.mark.context = this.brushFrames[view.index % 3][frame];
          view.brushFrame = frame;
          dirty = true;
        }
        if (progress === 1) view.crossAt = null;
      }
      if (view.errorUntil && time >= view.errorUntil) {
        view.errorUntil = 0;
        view.error.visible = false;
        dirty = true;
      }
      if (view.rig?.container.visible) {
        view.rig.update(time, this.reducedMotion());
        dirty = true;
      }
    }
    if (dirty) this.app.render();
  }
  refreshMotion() {
    if (!this.ready) return;
    for (const view of this.cells) {
      if (this.reducedMotion()) {
        view.entranceAt = null;
        view.entranceScale = 1;
        view.entranceY = 0;
        view.entranceAlpha = 1;
        view.pressMotion = null;
        view.pressScale = 1;
        this.scaleCell(view);
        if (view.crossAt !== null && view.crossAt !== undefined) {
          view.crossAt = null;
          view.brushFrame = CROSS_FRAMES;
          view.mark.context = this.brushFrames[view.index % 3][CROSS_FRAMES];
        }
      }
      view.rig?.update(this.clock.time, this.reducedMotion());
    }
    this.app.render();
    this.clock.wake();
  }
  destroy() {
    this.unsubscribe?.();
    this.resizeObserver?.disconnect();
    this.ready = false;
    this.destroyScene();
    if (this.brushFrames)
      for (const frames of this.brushFrames)
        for (const context of frames) context.destroy();
    for (const context of this.failedBrushes ?? []) context.destroy();
    this.app?.destroy(true, { children: true });
  }
}

/** Same renderer/input contract when WebGL is unavailable. */
export class DomBoardRenderer {
  async mount(host) {
    this.host = host;
    host.classList.add("dom-board");
  }
  render(model) {
    if (!this.host || (this.key === model.key && !model.effect.newPuzzle)) return;
    this.key = model.key;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    for (const [index, cell] of [...this.host.querySelectorAll(".cell-visual")].entries()) {
      cell.animate([
        { opacity: 0, transform: "translateY(-14px) scale(1.28)" },
        { opacity: 1, offset: 0.25 },
        { opacity: 1, transform: "translateY(0) scale(1)" },
      ], {
        duration: TILE_ENTRANCE_MS,
        delay: tileEntranceDelay(index, model.size),
        easing: "cubic-bezier(.16,1.15,.3,1)",
        fill: "both",
      });
    }
  }
  refreshMotion() {
    if (this.host)
      for (const cell of this.host.querySelectorAll(".cell"))
        for (const animation of cell.querySelector(".cell-visual").getAnimations())
          animation.cancel();
  }
  setPressed(index, pressed) {
    const cell = this.host
      ?.querySelector(".cell-grid")
      ?.children[index]?.querySelector(".cell-visual");
    if (!cell || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    cell.getAnimations().forEach((animation) => animation.cancel());
    if (pressed)
      cell.animate([{ transform: "scale(1)" }, { transform: "scale(.9)" }], {
        duration: 85,
        easing: "cubic-bezier(.2,.8,.2,1)",
        fill: "forwards",
      });
    else
      cell.animate(
        [
          { transform: "scale(.94)" },
          { transform: "scale(1.015)", offset: 0.65 },
          { transform: "scale(1)" },
        ],
        { duration: 235, easing: "cubic-bezier(.2,.8,.2,1)" },
      );
  }
  destroy() {
    this.host?.classList.remove("dom-board");
  }
}
